/* eslint-disable jsx-a11y/alt-text */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Utility from "../utility/utility";
import { BASE_URL, WEBSOCKET_URL } from "../services/environment";
import Gameboard, { socketEmitLeaveGame } from "./Gameboard";
import { io } from "socket.io-client";
import { useSelector } from "react-redux";
import useRest, { CALL_TYPES } from "../services/rest/api";
import { GAMELEAVE_URL } from "../services/apiUrl";

const RoomDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const gameData = useSelector((state) => state?.joinReducer || null);
  const [room, setRoom] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [socketEmit, setSocketEmit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);

  useEffect(() => {
    const adminId = localStorage.getItem("adminId");
    const apiKey = localStorage.getItem("adminApiKey");
    
    if (!adminId || !apiKey) {
      Utility.showToast("error", "Please login as admin first");
      navigate("/admin/auth");
      return;
    }
    
    fetchRoomDetails();
  }, [id, navigate]);

  // useEffect(async() => {

  //   if(socketEmit === true){
  //     console.log("inside socketEmit useEffect....");
  //     Utility.showToast("info", "Loading...");
  //     const socket = io(`${WEBSOCKET_URL}`);
  //     socket.connect()
  //     socket.emit("room_leave", "Hello from admin panel");

  //     console.log(socket.emit("room_leave", "Hello from admin panel"));

  //     // Utility.disconnectSocket();

  //     setSocketEmit(false);
  //   }
  // }, [socketEmit])

  const fetchRoomDetails = async () => {
    try {
      setLoading(true);
      
      const adminId = localStorage.getItem('adminId');
      const apiKey = localStorage.getItem('adminApiKey');
      
      const response = await fetch(`${BASE_URL}/api/admin/room/${id}/details`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          'admin-id': adminId,
          'api-key': apiKey
        }
      });

      const data = await response.json();

      if (data.status_code === 200) {
        setRoom(data.data);
        setMembers(data.data.members || []);
        setSelectedMembers([]);
        setLoading(false);
      } else {
        Utility.showToast("error", data.message);
        if (data.status_code === 401) {
          localStorage.removeItem('adminId');
          localStorage.removeItem('apiKey');
          navigate("/admin/auth");
        }
        setLoading(false);
      }
    } catch (error) {
      Utility.showToast("error", "Failed to fetch room details");
      setLoading(false);
    }
  };

  const handleSelectMember = (memberId) => {
    if (bulkMode) {
      if (selectedMembers.includes(memberId)) {
        setSelectedMembers(selectedMembers.filter(id => id !== memberId));
      } else {
        setSelectedMembers([...selectedMembers, memberId]);
      }
    }
  };

  const handleRemoveMember = async (memberId, memberName, isBulk = false) => {
    if (!isBulk && !window.confirm(`Are you sure you want to remove ${memberName} from this room?`)) {
      return;
    }

    if (isBulk && !window.confirm(`Are you sure you want to remove ${selectedMembers.length} selected members?`)) {
      return;
    }

    try {
      setRemoving(true);
      
      const adminId = localStorage.getItem('adminId');
      const apiKey = localStorage.getItem('adminApiKey');
      
      const memberIds = isBulk ? selectedMembers : [memberId];
      
      // First, get the room details to determine next turn
      const roomDetails = await fetchRoomDetailsForTurn(id);
      
      // Calculate next turn member
      const nextMemberId = calculateNextTurnMember(memberIds, roomDetails);
      
      // Remove each member
      const promises = memberIds.map(async (mid) => {
        return await removeMemberFromRoom(mid, id, nextMemberId);
      });

      const results = await Promise.all(promises);
      
      // Check if all removals were successful
      const allSuccess = results.every(result => result.success);
      
      if (allSuccess) {
        Utility.showToast("success", 
          isBulk 
            ? `${selectedMembers.length} members removed successfully` 
            : `${memberName} removed successfully`
        );
        
        // Clear selections
        setSelectedMembers([]);
        setBulkMode(false);
        
        // Refresh room details
        fetchRoomDetails();
      } else {
        Utility.showToast("error", "Some members could not be removed");
      }
    } catch (error) {
      Utility.showToast("error", "Failed to remove member(s)");
    } finally {
      setRemoving(false);
    }
  };

  const fetchRoomDetailsForTurn = async (roomId) => {
    try {
      const adminId = localStorage.getItem('adminId');
      const apiKey = localStorage.getItem('adminApiKey');
      
      const response = await fetch(`${BASE_URL}/api/admin/room/${roomId}/details`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
          'admin-id': adminId,
          'api-key': apiKey
        }
      });

      const data = await response.json();
      return data.status_code === 200 ? data.data : null;
    } catch (error) {
      console.error("Error fetching room details:", error);
      return null;
    }
  };

  const calculateNextTurnMember = (memberIdsToRemove, roomDetails) => {
    if (!roomDetails || !roomDetails.members) return 0;
    
    const currentMembers = roomDetails.members;
    const currentTurnMemberId = roomDetails.turnMemberId;
    
    // If the current turn member is being removed
    if (memberIdsToRemove.includes(currentTurnMemberId)) {
      // Find next available member in order
      const remainingMembers = currentMembers
        .filter(member => !memberIdsToRemove.includes(member.id))
        .sort((a, b) => a.id - b.id);
      
      if (remainingMembers.length > 0) {
        return remainingMembers[0].id;
      }
    }
    
    // If current turn member is not being removed, keep same
    return currentTurnMemberId;
  };

  const removeMemberFromRoom = async (memberId, roomId, nextUserTurn) => {
    try {
      const adminId = localStorage.getItem('adminId');
      const apiKey = localStorage.getItem('adminApiKey');
      
      // Call admin API to remove member
      const apiResponse = await fetch(`${BASE_URL}/api/admin/room/${roomId}/remove-member`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'admin-id': adminId,
          'api-key': apiKey
        },
        body: JSON.stringify({
          member_id: memberId,
          next_user_turn: nextUserTurn
        })
      });

      const apiData = await apiResponse.json();
      console.log("before after room leave api call....");
      
      if (apiData.status_code === 200) {

        console.log("inside after room leave api call....");
        // Emit socket event similar to player leaving
        //socketEmitLeaveGame();
        const socket_event = await emitSocketLeaveEvent(roomId, memberId, nextUserTurn);
        return { success: true, data: apiData };
      } else {
        return { success: false, error: apiData.message };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const { data, fetchData: LeaveGame } = useRest({
    URL: GAMELEAVE_URL,
    CALL_TYPE: CALL_TYPES.POST,
    PAYLOAD: {
      room_id: JSON.stringify(gameData?.data?.room_id),
      id: JSON.stringify(gameData?.data?.id),
    },
    lazy: true,
  });

  const emitSocketLeaveEvent = async (roomId, memberId, nextUserTurn) => {
    try {

      console.log("inside emitSocketLeaveEvent....");

      //Gameboard.Leave_game();

      setSocketEmit(true);

      const socket = io(`${WEBSOCKET_URL}`);
      socket.connect()

      // const socket = await Utility.getConnectedSocket();
      // console.log("socket = ", socket);
      socket.emit("room_leave", JSON.stringify({ room_id: roomId, next_user_turn: nextUserTurn }));

      console.log(socket.emit("admin_remove_member", JSON.stringify({ room_id: roomId, next_user_turn: nextUserTurn })));

      // Utility.disconnectSocket();
      // This would require a socket connection in admin panel
      // For now, we'll call a special admin socket endpoint
      // const response = await fetch(`${BASE_URL}/api/gameleave`, {
      //   method: "POST",
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'admin-id': localStorage.getItem('adminId'),
      //     'api-key': localStorage.getItem('adminApiKey')
      //   },
      //   body: JSON.stringify({
      //     room_id: roomId,
      //     id: `${memberId}`,
      //     //next_user_turn: nextUserTurn
      //   })
      // });

      
      //await LeaveGame(roomId, memberId);
      await clearGameStorage();
      
      //return await response.json();
    } catch (error) {
      console.error("Socket emit error:", error);
      // Continue even if socket fails - the API already updated database
    }
  };

  function clearGameStorage() {
    localStorage.removeItem('roomId');
    localStorage.removeItem('memberId');
    //localStorage.clear();
    
    sessionStorage.removeItem('roomId');
    sessionStorage.removeItem('memberId');
    sessionStorage.clear();

    // document.cookie.split(";").forEach((cookie) => {
    //   document.cookie = cookie
    //     .replace(/^ +/, "")
    //     .replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`);
    // });

    // if ("caches" in window) {
    //   caches.keys().then((names) => {
    //     names.forEach((name) => caches.delete(name));
    //   });
    // }
  }

  const handleSelectAll = () => {
    if (selectedMembers.length === members.length) {
      setSelectedMembers([]);
    } else {
      const allMemberIds = members.map(member => member.id);
      setSelectedMembers(allMemberIds);
    }
  };

  const formatAssets = (assets) => {
    if (!assets || typeof assets !== 'object') return 'N/A';
    
    return Object.entries(assets)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  if (loading) {
    return (
      <div className="background">
        <div className="container py-4 text-center">
          <div className="spinner-border text-white" role="status">
            <span className="visually-hidden"></span>
          </div>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="background">
        <div className="container py-4">
          <div className="alert alert-danger">Room not found</div>
          <button onClick={() => navigate("/admin/dashboard")} className="btn text-white">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="background">
      <div className="container py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="text-white mb-1">Room {room.id} Details</h1>
            {/* <p className="text-light mb-0">
              {room.memberCount} player(s) | Game: {room.gameStarted ? 'Started' : 'Not Started'} | 
              Turn: Player {room.turnMemberId}
            </p> */}
          </div>
          <div>
            <button 
              onClick={() => navigate("/admin/dashboard")}
              className="btn btn-info me-2 semi-bold"
            >
              Back to Dashboard
            </button>
            <button
              onClick={handleRoomRefresh}
              className="btn btn-danger me-2 semi-bold ml-2"
              disabled={removing}
            >
              {removing ? "Processing..." : "Room Refresh"}
            </button>
            <button 
              onClick={fetchRoomDetails}
              className="btn btn-info ml-2 semi-bold"
              disabled={removing}
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Room Info Card */}
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Room Information</h5>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-3">
                <p><strong>Room ID:</strong> {room.id}</p>
              </div>
              <div className="col-md-3">
                <p><strong>Players:</strong> {room.memberCount}/4</p>
              </div>
              {/* <div className="col-md-3">
                <p><strong>Game Status:</strong> 
                  <span className={`badge ${room.gameStarted ? 'bg-success' : 'bg-secondary'} ms-2`}>
                    {room.gameStarted ? 'In Progress' : 'Waiting'}
                  </span>
                </p>
              </div> */}
              <div className="col-md-3">
                <p><strong>Current Turn:</strong> {room.memberCount > 0 ? `Player ${room.turnMemberId}` : 'None'}</p>
              </div>
            </div>
            {/* <div className="row">
              <div className="col-md-6">
                <p><strong>Created:</strong> {new Date(room.createdAt).toLocaleString()}</p>
              </div>
            </div> */}
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {bulkMode && (
          <div className="alert alert-info d-flex justify-content-between align-items-center mb-4">
            <div>
              <strong>Bulk Mode Active</strong> - {selectedMembers.length} member(s) selected
            </div>
            <div>
              <button
                onClick={() => handleRemoveMember(null, null, true)}
                className="btn btn-danger btn-sm me-2"
                disabled={selectedMembers.length === 0 || removing}
              >
                {removing ? 'Removing...' : `Remove Selected (${selectedMembers.length})`}
              </button>
              <button
                onClick={() => {
                  setBulkMode(false);
                  setSelectedMembers([]);
                }}
                className="btn btn-secondary btn-sm ml-2"
                disabled={removing}
              >
                Cancel Bulk Mode
              </button>
            </div>
          </div>
        )}

        {/* Members Table */}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Room Members ({members.length})</h5>
            <div>
              {!bulkMode ? (
                <button
                  onClick={() => setBulkMode(true)}
                  className="btn btn-info btn-sm"
                >
                  Bulk Select
                </button>
              ) : (
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={selectedMembers.length === members.length}
                    onChange={handleSelectAll}
                    id="selectAll"
                  />
                  <label className="form-check-label" htmlFor="selectAll">
                    Select All
                  </label>
                </div>
              )}
            </div>
          </div>
          <div className="card-body">
            {members.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No members in this room</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      {bulkMode && <th style={{ width: '50px' }}></th>}
                      <th>ID</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Assets</th>
                      <th>Open Situations</th>
                      <th>Total Answers</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr 
                        key={member.id}
                        className={selectedMembers.includes(member.id) ? 'table-primary' : ''}
                        onClick={() => bulkMode && handleSelectMember(member.id)}
                        style={bulkMode ? { cursor: 'pointer' } : {}}
                      >
                        {bulkMode && (
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input ml-2"
                              checked={selectedMembers.includes(member.id)}
                              onChange={() => handleSelectMember(member.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                        )}
                        <td>{member.id}</td>
                        <td className="me-1">
                          {member.name}
                          {member.id === room.turnMemberId && (
                            <span className="badge bg-success ms-2">Current Turn</span>
                          )}
                          {member.isFacilitator && (
                            <span className="badge bg-info ms-2">Facilitator</span>
                          )}
                        </td>
                        <td>{member.isFacilitator ? 'Facilitator' : 'Player'}</td>
                        <td style={{ maxWidth: '200px' }}>
                          <small>{formatAssets(member.assets)}</small>
                        </td>
                        <td>{member.statistics?.openSituations || 0}</td>
                        <td>{member.statistics?.totalAnswers || 0}</td>
                        <td>{member.joinTime ? new Date(member.joinTime * 1000).toLocaleString() : 'N/A'}</td>
                        <td>
                          {!member.isFacilitator && (
                            <button
                              onClick={(e) => {
                                //e.stopPropagation();
                                handleRemoveMember(member.id, member.name, false);
                              }}
                              className="btn btn-sm btn-danger"
                              disabled={removing}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        {/* <div className="card border-danger mt-4">
          <div className="card-header bg-danger text-white">
            <h5 className="mb-0">Danger Zone</h5>
          </div>
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-8">
                <h6>Remove All Players</h6>
                <p className="text-muted mb-0">
                  Remove all players from this room and reset it to empty state.
                  This will also clear all game statistics and assets.
                </p>
              </div>
              <div className="col-md-4 text-end">
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to remove ALL players from this room? This action cannot be undone.')) {
                      handleRemoveAllMembers();
                    }
                  }}
                  className="btn btn-outline-danger"
                  disabled={removing || members.length === 0}
                >
                  {removing ? 'Processing...' : 'Remove All Players'}
                </button>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );

  async function handleRoomRefresh() {
    if (!window.confirm("Refresh this room?\n\nWarning: Do NOT use this during an ongoing game — it can cause game flow issues for connected players.\n\nUse this only when data did not update correctly in the database (e.g., due to network/socket issues) and you need to force-clean the room.\n\nIf you just want to remove a player, use the Remove button for that member instead.\n\nThis will remove all room members and clear assets/statistics for this room.")) {
      return;
    }

    try {
      setRemoving(true);
      setBulkMode(false);
      setSelectedMembers([]);

      const adminId = localStorage.getItem("adminId");
      const apiKey = localStorage.getItem("adminApiKey");

      const response = await fetch(`${BASE_URL}/api/admin/room/${id}/reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "admin-id": adminId,
          "api-key": apiKey,
        },
      });

      const data = await response.json();

      if (data.status_code === 200) {
        Utility.showToast("success", "Room refreshed successfully");
        await emitSocketRoomReset(id);
        fetchRoomDetails();
      } else {
        Utility.showToast("error", data.message || "Failed to refresh room");
      }
    } catch (error) {
      Utility.showToast("error", "Failed to refresh room");
    } finally {
      setRemoving(false);
    }
  }

  async function handleRemoveAllMembers() {
    try {
      setRemoving(true);
      
      const adminId = localStorage.getItem('adminId');
      const apiKey = localStorage.getItem('adminApiKey');
      
      const response = await fetch(`${BASE_URL}/api/admin/room/${id}/remove-all`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'admin-id': adminId,
          'api-key': apiKey
        }
      });

      const data = await response.json();
      
      if (data.status_code === 200) {
        Utility.showToast("success", "All players removed successfully");
        
        // Emit socket event to update all clients
        await emitSocketRoomReset(id);
        
        // Refresh room details
        fetchRoomDetails();
      } else {
        Utility.showToast("error", data.message);
      }
    } catch (error) {
      Utility.showToast("error", "Failed to remove all players");
    } finally {
      setRemoving(false);
    }
  }

  async function emitSocketRoomReset(roomId) {
    try {
      await fetch(`${BASE_URL}/api/admin/socket/room-reset`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'admin-id': localStorage.getItem('adminId'),
          'api-key': localStorage.getItem('adminApiKey')
        },
        body: JSON.stringify({
          room_id: roomId
        })
      });
    } catch (error) {
      console.error("Socket reset error:", error);
    }
  }
};

export default RoomDetails;